import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  FaLock,
  FaUser,
  FaStar,
  FaPhone,
  FaHome,
  FaCamera,
} from "react-icons/fa";
import GlobalLoader from "./GlobalLoader";
import { useNavigate } from "react-router-dom";
const ProfileDetailPage = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const [hasMembership, setHasMembership] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasPending, setHasPending] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const calculateAge = (dob) => dayjs().diff(dayjs(dob), "year");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, membershipRes] = await Promise.all([
          axios.get(`/api/matrimony-profile/${id}/`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }),
          axios.get(`/api/memberships/check-membership/`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }),
        ]);
        setProfile(profileRes.data);
        setHasMembership(membershipRes.data.has_membership);
      } catch (error) {
        console.error(error.response?.data || error.message);
        if (error.response?.status === 404) {
          toast.error("Profile unavailable or has been deactivated.");
          setError("Profile unavailable or has been deactivated.");
        } else {
          toast.error("An error occurred while fetching the profile.");
          setError("An error occurred while fetching the profile.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const checkPending = async () => {
      try {
        const res = await axios.get("/api/memberships/pending-check/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        if (res.data.has_pending) {
          setHasPending(true);
          setPendingPlan(res.data.plan_name);
        }
      } catch (error) {
        console.error("Error checking pending status", error);
      }
    };
    checkPending();
  }, []);

  if (loading) {
    return <GlobalLoader />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center text-gray-600">
        <img
          src={require("../../assets/default_image.webp")}
          alt="Profile Unavailable"
          className="w-48 h-48 mb-4"
        />
        <h2 className="text-xl font-semibold mb-2">Profile Unavailable</h2>
        <p className="max-w-md">
          The profile you are trying to view is unavailable or has been
          deactivated by the user. Please explore other profiles that match your
          preferences.
        </p>
        <button
          onClick={() => navigate("/matches-all")}
          className="mt-4 inline-block bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Browse Matches
        </button>
      </div>
    );
  }

  if (!profile) {
    return null; // fallback, should not happen but safe
  }

  return (
    <div className="w-full max-w-screen-xl mx-auto p-6 bg-white rounded shadow">
      {/* Top Profile Overview */}
      <div className="flex flex-col md:flex-row gap-6">
        <img
          src={profile.photo || require("../../assets/default_image.webp")}
          alt={profile.user.first_name}
          className="w-full md:w-64 h-64 object-cover rounded shadow"
        />
        <div className="flex-1 space-y-2">
          <h2 className="text-2xl font-bold">
            {profile.user.first_name} {profile.user.last_name}
            {profile.profile_completed && (
              <span
                className="ml-3 text-yellow-500"
                title="Profile 100% Completed"
              >
                <FaStar className="inline-block mb-1" />
              </span>
            )}
          </h2>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              {calculateAge(profile.dob)} yrs • {profile.height_feet}'
              {profile.height_inches}" • {profile.caste} •{" "}
              {profile.highest_education} • {profile.employed_in} •{" "}
              {profile.work_location_city} • {profile.work_location_state}
            </p>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <Section title="Personal Information" icon={<FaUser />}>
        <GridInfo>
          <InfoItem label="Age" value={`${calculateAge(profile.dob)} Years`} />
          <InfoItem
            label="Height"
            value={`${profile.height_feet}'${profile.height_inches}"`}
          />
          <InfoItem
            label="Languages"
            value={profile.mother_tongue || "Not specified"}
          />
          <InfoItem
            label="Kuldevi / kuldevta"
            value={`${profile.kuldevi || "Not specified"} / ${
              profile.kuldevta || "Not specified"
            }`}
          />
          <InfoItem label="kul" value={profile.kul || "Not specified"} />
          <InfoItem
            label="Date Of Birth"
            value={hasMembership ? profile.dob : <LockedItem />}
          />
          <InfoItem label="Marital Status" value={profile.marital_status} />
          <InfoItem label="Manglik" value={profile.manglik} />
          <InfoItem
            label="Lives In"
            value={`${profile.work_location_city} / ${profile.work_location_state}`}
          />
          <InfoItem label="Employment" value={profile.employed_in} />
          <InfoItem label="Education" value={profile.highest_education} />
          <InfoItem label="college name" value={profile.college_name} />
          <InfoItem label="Job Company Name" value={profile.job_company_name} />

          <InfoItem
            label="Vehicles"
            value={profile.vehicles || "Not specified"}
          />
          <InfoItem label="House" value={profile.House || "Not specified"} />
          <InfoItem label="Land" value={profile.land || "Not specified"} />
          <InfoItem
            label="business"
            value={profile.business || "Not specified"}
          />
          <InfoItem label="Physical status" value={profile.physical_status} />
          <InfoItem label="weight" value={profile.weight} />
          <InfoItem
            label="Drinking / Eating / Smoking  Habits"
            value={`${profile.drinking_habits || "Not specified"} / ${
              profile.eating_habits || "Not specified"
            } / ${profile.smoking_habits || "Not specified"}`}
          />
          <InfoItem
            label="Studied at"
            value={
              hasMembership ? (
                profile.college || "Not specified"
              ) : (
                <LockedItem />
              )
            }
          />
          <InfoItem
            label="Works at"
            value={
              hasMembership ? (
                profile.company || "Not specified"
              ) : (
                <LockedItem />
              )
            }
          />
        </GridInfo>
      </Section>

      {/* Family Information */}
      <Section title="Family Information" icon={<FaHome />}>
        <GridInfo>
          <InfoItem
            label="Belong From"
            value={profile.ancestral_origin || "Not specified"}
          />
          <InfoItem
            label="Family Type"
            value={profile.family_type || "Not specified"}
          />
          <InfoItem
            label="Family Values"
            value={profile.family_values || "Not specified"}
          />
          <InfoItem
            label="Family Status"
            value={profile.family_status || "Not specified"}
          />
          <InfoItem label="Vansh" value={profile.vansh || "Not specified"} />
          <InfoItem
            label="Father's Occupation"
            value={profile.fathers_occupation || "Not specified"}
          />
          <InfoItem
            label="Mother's Occupation"
            value={profile.mothers_occupation || "Not specified"}
          />
          <InfoItem
            label="Brothers / Sisters"
            value={`${profile.no_of_brothers || "Not specified"} / ${
              profile.no_of_sisters || "Not specified"
            }`}
          />
          <InfoItem
            label="Brothers Qualification / Occupation"
            value={`${profile.brother_qualification || "Not specified"} / ${
              profile.brother_occupation || "Not specified"
            }`}
          />
          <InfoItem
            label="Sister Qualification / Occupation"
            value={`${profile.sister_qualification || "Not specified"} / ${
              profile.sister_occupation || "Not specified"
            }`}
          />
        </GridInfo>
      </Section>
      <Section title="Maternal Family Information" icon={<FaHome />}>
        <GridInfo>
          <InfoItem
            label="Belong From"
            value={profile.mama_place || "Not specified"}
          />
          <InfoItem
            label="Mama Name"
            value={profile.mama_name || "Not specified"}
          />
          <InfoItem
            label="Nana Name"
            value={profile.nana_name || "Not specified"}
          />
          <InfoItem
            label="Gotra"
            value={profile.mama_gotra || "Not specified"}
          />
          <InfoItem label="kuldevi" value={profile.mama_kuldevi || "Not specified"} />
        </GridInfo>
      </Section>

      {/* Contact Information */}
      <Section title="Contact Information" icon={<FaPhone />}>
        <InfoItem
          label="Mobile Number"
          value={hasMembership ? profile.phone : <LockedItem />}
        />
      </Section>
      <Section title="Self Introduction Video" icon={<FaCamera />}>
        {hasMembership ? (
          profile.intro_video ? (
            <a
              href={profile.intro_video}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Click here - Watch Introduction Video
            </a>
          ) : (
            <p className="text-gray-500 italic">No video uploaded yet.</p>
          )
        ) : (
          <LockedItem />
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="mt-6 border-t pt-4">
    <h3 className="text-lg font-semibold flex items-center gap-2 text-pink-600">
      {icon} {title}
    </h3>
    <div className="mt-2 text-sm">{children}</div>
  </div>
);

const GridInfo = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const InfoItem = ({ label, value }) => (
  <p>
    <span className="font-medium">{label}:</span>{" "}
    <span className="ml-1">{value}</span>
  </p>
);

const LockedItem = () => {
  const navigate = useNavigate();

  return (
    <span
      className="flex items-center text-gray-500 cursor-pointer hover:text-pink-600"
      onClick={() => navigate("/membership-pricing")}
    >
      <FaLock className="mr-1" /> Upgrade to view
    </span>
  );
};
export default ProfileDetailPage;
